import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormInput,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';

// Form schema
interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  category: string;
  priority: string;
  notifications: boolean;
  terms: boolean;
  date: Date;
}

export function FormSection() {
  const form = useForm<FormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
      category: '',
      priority: 'medium',
      notifications: false,
      terms: false,
      date: new Date(),
    },
  });

  const onSubmit = (_data: FormData) => {
    alert('Form submitted successfully!');
  };

  return (
    <Card className='max-w-4xl mx-auto'>
      <CardHeader>
        <CardTitle>Contact Form</CardTitle>
        <CardDescription>Complete form with validation and all input types</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                control={form.control}
                name='name'
                required
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <FormInput
                    label='Full Name'
                    description='Your complete name as it appears on official documents'
                    placeholder='Enter your full name'
                    size='large'
                    {...field}
                  />
                )}
              />

              <FormField
                control={form.control}
                name='email'
                required
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field }) => (
                  <FormInput
                    label='Email Address'
                    description="We'll use this to contact you"
                    type='email'
                    placeholder='Enter your email'
                    size='large'
                    {...field}
                  />
                )}
              />

              <FormField
                control={form.control}
                name='phone'
                rules={{
                  pattern: {
                    value: /^[+]?[1-9][\d]{0,15}$/,
                    message: 'Invalid phone number',
                  },
                }}
                render={({ field }) => (
                  <FormInput
                    label='Phone Number'
                    description='Optional contact number'
                    type='tel'
                    placeholder='Enter your phone number'
                    size='small'
                    {...field}
                  />
                )}
              />

              <FormField
                control={form.control}
                name='category'
                rules={{ required: 'Category is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Select a category' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='general'>General Inquiry</SelectItem>
                        <SelectItem value='support'>Technical Support</SelectItem>
                        <SelectItem value='billing'>Billing Question</SelectItem>
                        <SelectItem value='feedback'>Feedback</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>What is this regarding?</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='priority'
                render={({ field }) => (
                  <FormItem className='space-y-3'>
                    <FormLabel>Priority Level</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className='flex flex-col space-y-1'
                      >
                        <div className='flex items-center space-x-2'>
                          <RadioGroupItem value='low' id='priority-low' />
                          <FormLabel htmlFor='priority-low'>Low</FormLabel>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <RadioGroupItem value='medium' id='priority-medium' />
                          <FormLabel htmlFor='priority-medium'>Medium</FormLabel>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <RadioGroupItem value='high' id='priority-high' />
                          <FormLabel htmlFor='priority-high'>High</FormLabel>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormDescription>How urgent is your request?</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='date'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Contact Date</FormLabel>
                    <FormControl>
                      <DatePicker value={field.value} onChange={field.onChange} placeholder='Select a date' />
                    </FormControl>
                    <FormDescription>When would you like us to contact you?</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='message'
              rules={{ required: 'Message is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea placeholder='Type your message here...' className='min-h-[120px]' {...field} />
                  </FormControl>
                  <FormDescription>Tell us what you need help with</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormField
                control={form.control}
                name='notifications'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                    <div className='space-y-0.5'>
                      <FormLabel className='text-base'>Email Notifications</FormLabel>
                      <FormDescription>Receive updates about your request</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='terms'
                rules={{ required: 'You must accept the terms' }}
                render={({ field }) => (
                  <FormItem className='flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4'>
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className='space-y-1 leading-none'>
                      <FormLabel>I accept the terms and conditions</FormLabel>
                      <FormDescription>You must agree to our terms to continue</FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <div className='flex gap-4 pt-4'>
              <Button type='button' variant='outline' className='flex-1'>
                Cancel
              </Button>
              <Button type='submit' variant='enabled' className='flex-1'>
                Submit Form
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
